/**
 * Money inside the policy gate is a decimal string with at most six places (the precision of
 * `Execution.cost` and `Budget.spent`, `Decimal(14, 6)`). Every comparison and every sum goes
 * through integer micros (`bigint`), never `parseFloat`: 0.1 + 0.2 must be 0.3 exactly, and a
 * budget must never be crossed (or missed) because of binary rounding.
 *
 * No Node-only imports here: the browser loads this module from source through the Vite alias.
 */

/** Micros per unit: 10^6, i.e. six decimal places. */
export const MICROS_PER_UNIT = 1_000_000n;

const SCALE = 6;
const DECIMAL = /^(-?)(\d+)(?:\.(\d{1,6}))?$/;

/** A decimal string with at most six places, e.g. `"12.5"`, `"0.000001"`, `"-2.25"`. */
export type Money = string;

/** Exact micros of a decimal string. Throws `RangeError` for anything else (floats, exponents, 7+ places). */
export function toMicros(value: Money): bigint {
  const m = DECIMAL.exec(value.trim());
  if (!m) throw new RangeError(`not a decimal amount with at most ${SCALE} places: ${JSON.stringify(value)}`);
  const [, sign, whole, fraction = ''] = m as unknown as [string, string, string, string | undefined];
  const micros = BigInt(whole) * MICROS_PER_UNIT + BigInt(fraction.padEnd(SCALE, '0'));
  return sign === '-' ? -micros : micros;
}

/** Micros back as a decimal string with exactly six places (round-trips through `toMicros`). */
export function fromMicros(micros: bigint): Money {
  const negative = micros < 0n;
  const abs = negative ? -micros : micros;
  const whole = abs / MICROS_PER_UNIT;
  const fraction = (abs % MICROS_PER_UNIT).toString().padStart(SCALE, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** -1 / 0 / 1, by value: `compareMoney('1.10', '1.1') === 0`. */
export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  const left = toMicros(a);
  const right = toMicros(b);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

/** Sum of any number of amounts as a six-place decimal string (no arguments → `"0.000000"`). */
export function addMoney(...values: Money[]): Money {
  let total = 0n;
  for (const v of values) total += toMicros(v);
  return fromMicros(total);
}

/** The smaller of two amounts, returned as the caller wrote it (formatting is never normalised here). */
export function minMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) <= 0 ? a : b;
}

/** The larger of two amounts, returned as the caller wrote it. */
export function maxMoney(a: Money, b: Money): Money {
  return compareMoney(a, b) >= 0 ? a : b;
}

/** `a > b` by value. */
export function isGreater(a: Money, b: Money): boolean {
  return compareMoney(a, b) > 0;
}
